<template>
  <div v-intersect="handleInView">
    <video
      v-bind="omit({ ...$attrs, src: undefined })"
      ref="video"
      :poster="displayPoster"
      preload="metadata"
      :data-src="dataSrc || src"
      :muted="muted"
      :loop="loop"
      x5-video-player-type
      x5-video-player-fullscreen
      x-webkit-airplay
      webkit-playsinline
      playsinline
      @play="handlePlay"
    >
      <template v-if="shouldLoad">
        <source
          v-for="(source, index) in computedVideoSources"
          :key="index"
          :src="source.src"
          :type="source.type"
        />
      </template>
      <slot />
    </video>
  </div>
</template>

<script lang="ts" setup>
import { useBreakpoint } from '@rendora/design/index';
import { omit } from 'lodash-es';
import videoManager from '~/components/video-player/store';
import { transformImage } from '~/utils';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

interface VideoHTMLAttributes {
  src?: string;
  dataSrc?: string; // SEO 友好的视频源属性
  sources?: Array<{ src: string; type?: string }>; // 多个视频源支持
  lazy?: boolean; // 是否启用懒加载
  poster?: string;
  loop?: boolean;
  muted?: boolean;
}

const displayPoster = computed(() => {
  const posterLink = transformImage(props.poster, { width: 1048 });
  return posterLink;
});

const video = ref<HTMLVideoElement>();
const shouldLoad = ref(false);

const props = withDefaults(defineProps<VideoHTMLAttributes>(), {
  lazy: true,
  loop: true,
  muted: true,
});

// 生成 MP4 fallback URL
const getMp4FallbackUrl = (url: string): string => {
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${url}.mp4`;
  }
  return `${url.substring(0, lastDotIndex)}.mp4`;
};

// 构建视频源列表
const computedVideoSources = computed(() => {
  // 在 SSR 时只使用 isMobile，避免访问 window 对象导致构建错误
  // isMobile 应该已经提供了正确的值，不需要额外检查 ontouchstart
  const mobile = isMobile.value;
  let sourcesToUse: Array<{ src: string; type?: string }> = [];

  // 如果有 sources 配置，使用它
  if (props.sources && props.sources.length > 0) {
    sourcesToUse = [...props.sources];
  } else {
    // 如果只有单个 src 或 dataSrc，添加 mp4 fallback
    const videoUrl = props.dataSrc || props.src;
    if (!videoUrl) {
      return [];
    }

    const ext = videoUrl.split('.').pop()?.toLowerCase();
    if (ext) {
      const typeMap: Record<string, string> = {
        m3u8: 'application/x-mpegURL',
        mp4: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
      };

      const originalSource = {
        src: videoUrl,
        type: typeMap[ext] || `video/${ext}`,
      };

      // 如果不是 mp4，添加 mp4 fallback
      if (ext !== 'mp4') {
        const mp4Fallback = {
          src: getMp4FallbackUrl(videoUrl),
          type: 'video/mp4',
        };

        // 移动端：mp4 放在前面，优先使用
        if (mobile) {
          sourcesToUse.push(mp4Fallback);
          sourcesToUse.push(originalSource);
        } else {
          // PC端：原始格式放前面
          sourcesToUse.push(originalSource);
          sourcesToUse.push(mp4Fallback);
        }
      } else {
        sourcesToUse.push(originalSource);
      }
    } else {
      sourcesToUse.push({ src: videoUrl });
    }
  }

  // 如果是移动端且有多个sources，确保mp4优先
  if (mobile && sourcesToUse.length > 1) {
    // 将mp4格式的source移到前面
    const mp4Sources = sourcesToUse.filter(s => s.type?.includes('mp4'));
    const otherSources = sourcesToUse.filter(s => !s.type?.includes('mp4'));
    return [...mp4Sources, ...otherSources];
  }

  return sourcesToUse;
});

// 处理元素进入视口
const handleInView = (inView: boolean) => {
  if (!video.value) return;
  if (inView) {
    shouldLoad.value = true;
  } else {
    shouldLoad.value = false;
  }
};

const handlePlay = () => {
  if (video.value) {
    videoManager.setCurrentVideoPlayer(video.value);
  }
};

watch(
  () => [props.src, props.dataSrc, props.sources, shouldLoad.value],
  () => {
    if (
      (props.src || props.dataSrc || (props.sources && props.sources.length > 0)) &&
      shouldLoad.value
    ) {
      checkHLSSupport();
    }
  },
);

async function patchHLS(source: string) {
  const Hls = await import('hls.js').then(m => m.default);
  if (
    video.value &&
    !video.value!.canPlayType('application/vnd.apple.mpegurl') &&
    Hls.isSupported()
  ) {
    const hls = new Hls();
    hls.loadSource(source);
    hls.attachMedia(video.value);
  }
}

function checkHLSSupport() {
  const videoSrc = props.dataSrc || props.src;

  if (videoSrc?.endsWith('.m3u8')) {
    patchHLS(videoSrc);
    return;
  }

  // source detect
  const sources = video.value?.getElementsByTagName('source');
  if (!sources) {
    return;
  }

  if (sources.length > 0) {
    for (const source of sources) {
      if (source.src.endsWith('.m3u8')) {
        patchHLS(source.src);
        break;
      }
    }
  }
}

const observer = ref<MutationObserver | null>(null);

onMounted(() => {
  // 即使 lazy=false，也等待进入视口后再加载，确保真正的懒加载
  // 如果需要立即加载，可以在父组件中通过其他方式控制
  // if (!props.lazy) {
  //   shouldLoad.value = true
  // }

  observer.value = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        checkHLSSupport();
      }
    }
  });
  if (video.value) {
    observer.value.observe(video.value, { childList: true });
    // 只有在应该加载时才检查 HLS 支持
    if (shouldLoad.value) {
      checkHLSSupport();
    }
  }
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
  observer.value = null;
});

defineExpose({
  video,
  shouldLoad,
});
</script>

<style scoped lang="scss">
video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
